import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BalanceIcon from '@mui/icons-material/Balance';
import { colors, shadows, borderRadius } from '../theme/designSystem';
import { substratesService } from '../services/api';
import {
  MonthlyReconciliationReport,
  SubstrateDelivery,
  CreateSubstrateDeliveryDto,
  CommodityCode,
} from '../types';

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const COMMODITY_COLORS: Record<CommodityCode, string> = {
  PW: '#e3f2fd', EW: '#f3e5f5', FW: '#e8f5e9', '104': '#fff8e1',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtMT = (v: number | undefined | null, decimals = 4): string => {
  if (v === undefined || v === null || v === 0) return '-';
  return v.toFixed(decimals);
};

const fmtMTFull = (v: number | undefined | null, decimals = 4): string => {
  if (v === undefined || v === null) return '0.0000';
  return v.toFixed(decimals);
};

const rowBg = (idx: number) => (idx % 2 === 0 ? '#ffffff' : '#f9fafb');

// ─── Types ────────────────────────────────────────────────────────────────────

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

// ─── Empty form ───────────────────────────────────────────────────────────────

const emptyForm = (): CreateSubstrateDeliveryDto => ({
  date: new Date().toISOString().split('T')[0],
  commodity_code: 'PW',
  net_mt: undefined,
  new_weight_mt: undefined,
  ds_content: undefined,
  prorated: 14.0,
  diverted_composting_mt: 0,
  source: 'DOLE',
});

// ─── Component ────────────────────────────────────────────────────────────────

const SubstratesReconciliation: React.FC = () => {
  const now = new Date();
  const [tab, setTab] = useState(0);

  const [report, setReport] = useState<MonthlyReconciliationReport | null>(null);
  const [reportYear, setReportYear] = useState(now.getFullYear());
  const [reportMonth, setReportMonth] = useState(now.getMonth() + 1);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const [deliveries, setDeliveries] = useState<SubstrateDelivery[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateSubstrateDeliveryDto>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ─── Load report ─────────────────────────────────────────────────────────────

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    setReportError(null);
    try {
      const data = await substratesService.getMonthlyReport(reportYear, reportMonth);
      setReport(data);
    } catch (err: any) {
      setReportError(err?.response?.data?.message || 'Failed to load report');
    } finally {
      setReportLoading(false);
    }
  }, [reportYear, reportMonth]);

  useEffect(() => { if (tab === 0) loadReport(); }, [tab, loadReport]);

  // ─── Load records ─────────────────────────────────────────────────────────────

  const loadDeliveries = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const y = String(reportYear);
      const m = String(reportMonth).padStart(2, '0');
      const lastDay = new Date(reportYear, reportMonth, 0).getDate();
      const data = await substratesService.getDeliveries({
        startDate: `${y}-${m}-01`,
        endDate: `${y}-${m}-${String(lastDay).padStart(2, '0')}`,
      });
      setDeliveries(data);
    } catch { /* silent */ } finally {
      setRecordsLoading(false);
    }
  }, [reportYear, reportMonth]);

  useEffect(() => { if (tab === 1) loadDeliveries(); }, [tab, loadDeliveries]);

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleOpenAdd = () => { setEditingId(null); setForm(emptyForm()); setSaveError(null); setDialogOpen(true); };

  const handleOpenEdit = (d: SubstrateDelivery) => {
    setEditingId(d.id);
    setForm({
      date: d.date, dole_no: d.dole_no, dr_no: d.dr_no, commodity_code: d.commodity_code,
      commodity_name: d.commodity_name, inbound_time: d.inbound_time, outbound_time: d.outbound_time,
      net_mt: d.net_mt, ds_content: d.ds_content, prorated: d.prorated, new_weight_mt: d.new_weight_mt,
      diverted_composting_mt: d.diverted_composting_mt, remarks1: d.remarks1, remarks2: d.remarks2,
      notes: d.notes, source: d.source, field: d.field, date_received: d.date_received,
    });
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this delivery record?')) return;
    try { await substratesService.deleteDelivery(id); setDeliveries((p) => p.filter((d) => d.id !== id)); }
    catch { alert('Failed to delete.'); }
  };

  const handleSave = async () => {
    setSaving(true); setSaveError(null);
    try {
      if (editingId) await substratesService.updateDelivery(editingId, form);
      else await substratesService.createDelivery(form);
      setDialogOpen(false); loadDeliveries();
      if (tab === 0) loadReport();
    } catch (err: any) { setSaveError(err?.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleFieldChange = (field: keyof CreateSubstrateDeliveryDto, value: any) =>
    setForm((p) => ({ ...p, [field]: value }));

  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  // ─── Table cell helpers ───────────────────────────────────────────────────────

  const hCell = (
    label: string,
    align: 'center' | 'left' | 'right' = 'center',
    span = 1,
    rowSpan = 1,
    bg = '#1a5276',
  ) => (
    <TableCell align={align} colSpan={span} rowSpan={rowSpan}
      sx={{ backgroundColor: bg, color: '#fff', fontWeight: 700, fontSize: '0.68rem',
        border: '1px solid #34495e', padding: '4px 6px', lineHeight: 1.2, whiteSpace: 'pre-line' }}>
      {label}
    </TableCell>
  );

  const dCell = (
    value: string | number,
    bg = '#fff', bold = false,
    align: 'center' | 'left' | 'right' = 'center',
  ) => (
    <TableCell align={align}
      sx={{ backgroundColor: bg, fontWeight: bold ? 700 : 400, fontSize: '0.72rem',
        border: '1px solid #ddd', padding: '3px 6px', whiteSpace: 'nowrap' }}>
      {value}
    </TableCell>
  );

  // ─── Report ───────────────────────────────────────────────────────────────────

  const renderReport = () => {
    if (reportLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    if (reportError) return <Alert severity="error">{reportError}</Alert>;
    if (!report) return null;

    const { days, totals } = report;

    return (
      <Box>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Month</InputLabel>
            <Select value={reportMonth} label="Month" onChange={(e) => setReportMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel>Year</InputLabel>
            <Select value={reportYear} label="Year" onChange={(e) => setReportYear(Number(e.target.value))}>
              {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" size="small" onClick={loadReport}>Refresh</Button>
          <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}>Print</Button>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" label={`PW: ${fmtMTFull(totals.pw_mt)} MT`} sx={{ backgroundColor: '#e3f2fd' }} />
          <Chip size="small" label={`FW: ${fmtMTFull(totals.fw_mt)} MT`} sx={{ backgroundColor: '#e8f5e9' }} />
          <Chip size="small" label={`EW: ${fmtMTFull(totals.ew_mt)} MT`} sx={{ backgroundColor: '#f3e5f5' }} />
          <Chip size="small" label={`Total: ${fmtMTFull(totals.total_mt)} MT`} color="primary" />
        </Stack>

        <Box id="printable-report">
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>SURALLAH BIOGAS VENTURES CORP.</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 1 }}>DAILY WASTES MONITORING</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {report.month_name.toUpperCase()} 1-{new Date(report.year, report.month, 0).getDate()}, {report.year}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* ── Main Table ── */}
            <Box sx={{ flex: '1 1 auto', overflowX: 'auto' }}>
              <TableContainer component={Paper} sx={{ boxShadow: shadows.card, borderRadius: borderRadius.card }}>
                <Table size="small" sx={{ borderCollapse: 'collapse', minWidth: 760 }}>
                  <TableHead>
                    <TableRow>
                      {hCell('DATE', 'center', 1, 2)}
                      {hCell('DELIVERED TO BIOGAS DPO', 'center', 4, 1, '#1e6b2e')}
                      {hCell('TOTAL', 'center', 1, 2, '#1a6b3a')}
                      {hCell('DS ANALYSIS', 'center', 2, 1, '#6a3093')}
                      {hCell('Diverted to Composting\nClean up', 'center', 1, 2, '#c0392b')}
                      {hCell('TOTAL', 'center', 1, 2, '#145a32')}
                    </TableRow>
                    <TableRow>
                      {hCell('PW', 'center', 1, 1, '#1e8449')}
                      {hCell('EW', 'center', 1, 1, '#1e8449')}
                      {hCell('FW', 'center', 1, 1, '#1e8449')}
                      {hCell('104', 'center', 1, 1, '#1e8449')}
                      {hCell('NEW WEIGHT\n(MT)', 'center', 1, 1, '#7d3c98')}
                      {hCell('TOTAL NEW\nWEIGHT (MT)', 'center', 1, 1, '#7d3c98')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {days.map((day, idx) => {
                      const bg = rowBg(idx);
                      const [y, mo, dd] = day.date.split('-');
                      const dateLabel = `${parseInt(dd)}-${new Date(Number(y), Number(mo) - 1, 1).toLocaleString('en', { month: 'short' })}-${y.slice(2)}`;
                      const hasData = day.total_delivered_mt > 0;
                      const dsNew = day.ds_new_weight_mt ?? 0;
                      const dsTotal = day.total_new_weight_mt ?? dsNew;
                      const diverted = day.diverted_composting_mt ?? 0;
                      return (
                        <TableRow key={day.date} sx={{ '&:hover': { backgroundColor: '#f0f4ff' } }}>
                          {dCell(dateLabel, bg, false, 'left')}
                          {dCell(fmtMT(day.pw_mt), hasData && day.pw_mt > 0 ? '#d5f5e3' : bg)}
                          {dCell(fmtMT(day.ew_mt), hasData && day.ew_mt > 0 ? '#d5f5e3' : bg)}
                          {dCell(fmtMT(day.fw_mt), hasData && day.fw_mt > 0 ? '#d5f5e3' : bg)}
                          {dCell(fmtMT(day.mt104_mt), hasData && day.mt104_mt > 0 ? '#d5f5e3' : bg)}
                          {dCell(fmtMT(day.total_delivered_mt), hasData ? '#a9dfbf' : bg, hasData)}
                          {dCell(dsNew > 0 ? dsNew.toFixed(2) : '-', hasData && dsNew > 0 ? '#d7bde2' : bg)}
                          {dCell(dsTotal > 0 ? dsTotal.toFixed(2) : '-', hasData && dsTotal > 0 ? '#d7bde2' : bg)}
                          {dCell(diverted > 0 ? diverted.toFixed(4) : '-', diverted > 0 ? '#fadbd8' : bg)}
                          {dCell(fmtMT(day.total_mt), hasData ? '#a9dfbf' : bg, hasData)}
                        </TableRow>
                      );
                    })}
                    {/* Totals row */}
                    <TableRow>
                      {dCell('TOTAL', '#ffd54f', true, 'left')}
                      {dCell(fmtMTFull(totals.pw_mt), '#ffd54f', true)}
                      {dCell(fmtMTFull(totals.ew_mt), '#ffd54f', true)}
                      {dCell(fmtMTFull(totals.fw_mt), '#ffd54f', true)}
                      {dCell(fmtMTFull(totals.mt104_mt), '#ffd54f', true)}
                      {dCell(fmtMTFull(totals.total_delivered_mt), '#ffd54f', true)}
                      {dCell(totals.ds_new_weight_mt ? totals.ds_new_weight_mt.toFixed(2) : '-', '#ffd54f', true)}
                      {dCell(totals.total_new_weight_mt ? totals.total_new_weight_mt.toFixed(2) : '-', '#ffd54f', true)}
                      {dCell(totals.diverted_composting_mt ? totals.diverted_composting_mt.toFixed(4) : '-', '#ffd54f', true)}
                      {dCell(fmtMTFull(totals.total_mt), '#ffd54f', true)}
                    </TableRow>
                    {/* Percentage row */}
                    <TableRow>
                      {dCell('%', '#eceff1', true)}
                      {dCell(`${report.pw_pct}%`, '#eceff1')}
                      {dCell(`${report.ew_pct}%`, '#eceff1')}
                      {dCell(`${report.fw_pct}%`, '#eceff1')}
                      {dCell(`${report.mt104_pct}%`, '#eceff1')}
                      {dCell('100%', '#eceff1', true)}
                      {dCell('', '#eceff1')}
                      {dCell('', '#eceff1')}
                      {dCell('', '#eceff1')}
                      {dCell('', '#eceff1')}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* ── Truck Loads Table ── */}
            <Box sx={{ flex: '0 0 auto', minWidth: 220 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, fontSize: '0.72rem', lineHeight: 1.3 }}>
                TRUCK LOADS DELIVERED TO BIOGAS<br />RECORD FROM DOLE
              </Typography>
              <TableContainer component={Paper} sx={{ boxShadow: shadows.card, borderRadius: borderRadius.card }}>
                <Table size="small" sx={{ borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow>
                      {hCell('PW', 'center', 1, 1, '#1e6b2e')}
                      {hCell('EW', 'center', 1, 1, '#1e6b2e')}
                      {hCell('FW', 'center', 1, 1, '#1e6b2e')}
                      {hCell('TOTAL', 'center', 1, 1, '#145a32')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {days.map((day, idx) => {
                      const bg = rowBg(idx);
                      const tpw = day.truck_pw ?? 0;
                      const tew = day.truck_ew ?? 0;
                      const tfw = day.truck_fw ?? 0;
                      const tt = day.truck_total ?? 0;
                      return (
                        <TableRow key={day.date} sx={{ '&:hover': { backgroundColor: '#f0f4ff' } }}>
                          {dCell(tpw > 0 ? tpw : '-', bg)}
                          {dCell(tew > 0 ? tew : '-', bg)}
                          {dCell(tfw > 0 ? tfw : '-', bg)}
                          {dCell(tt > 0 ? tt : '-', bg, tt > 0)}
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      {dCell(totals.truck_pw ?? '-', '#ffd54f', true)}
                      {dCell(totals.truck_ew ?? '-', '#ffd54f', true)}
                      {dCell(totals.truck_fw ?? '-', '#ffd54f', true)}
                      {dCell(totals.truck_total ?? '-', '#ffd54f', true)}
                    </TableRow>
                    <TableRow>
                      {dCell('', '#eceff1')}
                      {dCell('', '#eceff1')}
                      {dCell('', '#eceff1')}
                      {dCell('', '#eceff1')}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* ── Legend ── */}
          <Box sx={{ mt: 2, p: 1.5, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fafafa', display: 'inline-block' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Legends:</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}><strong>PW | 104 | Transferred to Surallah</strong> - Pulp Waste</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}><strong>FW</strong> – Fruit Waste</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}><strong>EW</strong> – External Waste</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}><strong>104</strong> – Composting</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // ─── Records tab ──────────────────────────────────────────────────────────────

  const renderRecords = () => (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Month</InputLabel>
          <Select value={reportMonth} label="Month" onChange={(e) => setReportMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel>Year</InputLabel>
          <Select value={reportYear} label="Year" onChange={(e) => setReportYear(Number(e.target.value))}>
            {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" size="small" onClick={loadDeliveries}>Refresh</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>Add Delivery</Button>
      </Stack>

      {recordsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: shadows.card }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1a5276' }}>
                {['Date', 'DOLE/DR No.', 'Commodity', 'Inbound', 'Outbound', 'Net (MT)', 'DS%', 'New Wt (MT)', 'Diverted (MT)', 'Source', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ color: '#fff', fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4, color: '#888' }}>
                    No delivery records for {MONTHS[reportMonth - 1]} {reportYear}.<br />
                    Click "Add Delivery" to add records.
                  </TableCell>
                </TableRow>
              ) : (
                deliveries.map((d, idx) => (
                  <TableRow key={d.id} sx={{ backgroundColor: rowBg(idx), '&:hover': { backgroundColor: '#f0f4ff' } }}>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{d.date}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{d.dole_no || d.dr_no || '-'}</TableCell>
                    <TableCell>
                      <Chip label={d.commodity_code} size="small"
                        sx={{ backgroundColor: COMMODITY_COLORS[d.commodity_code as CommodityCode] ?? '#eee', fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{d.inbound_time || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{d.outbound_time || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{d.net_mt?.toFixed(4) || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{d.ds_content?.toFixed(2) || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{d.new_weight_mt?.toFixed(4) || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{d.diverted_composting_mt ? d.diverted_composting_mt.toFixed(4) : '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}><Chip label={d.source} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenEdit(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(d.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // ─── Dialog ───────────────────────────────────────────────────────────────────

  const renderDialog = () => (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#1a5276', color: '#fff', fontWeight: 700 }}>
        {editingId ? 'Edit Delivery Record' : 'Add Delivery Record'}
      </DialogTitle>
      <DialogContent dividers>
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={6}>
            <TextField label="Date *" type="date" value={form.date}
              onChange={(e) => handleFieldChange('date', e.target.value)}
              fullWidth size="small" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Commodity *</InputLabel>
              <Select value={form.commodity_code} label="Commodity *"
                onChange={(e) => handleFieldChange('commodity_code', e.target.value as CommodityCode)}>
                <MenuItem value="PW">PW – Pulp Pineapple</MenuItem>
                <MenuItem value="EW">EW – External Waste</MenuItem>
                <MenuItem value="FW">FW – Whole Pineapple</MenuItem>
                <MenuItem value="104">104 – Composting</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField label="DOLE No." value={form.dole_no || ''}
              onChange={(e) => handleFieldChange('dole_no', e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="DR No. (SBVC)" value={form.dr_no || ''}
              onChange={(e) => handleFieldChange('dr_no', e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select value={form.source || 'DOLE'} label="Source"
                onChange={(e) => handleFieldChange('source', e.target.value)}>
                <MenuItem value="DOLE">DOLE</MenuItem>
                <MenuItem value="SBVC">SBVC</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField label="Inbound Time" type="time" value={form.inbound_time || ''}
              onChange={(e) => handleFieldChange('inbound_time', e.target.value)}
              fullWidth size="small" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Outbound Time" type="time" value={form.outbound_time || ''}
              onChange={(e) => handleFieldChange('outbound_time', e.target.value)}
              fullWidth size="small" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}><Divider><Typography variant="caption">Weight Data (MT)</Typography></Divider></Grid>
          <Grid item xs={6}>
            <TextField label="Net Weight (MT)" type="number" inputProps={{ step: '0.0001', min: 0 }}
              value={form.net_mt ?? ''}
              onChange={(e) => handleFieldChange('net_mt', e.target.value === '' ? undefined : Number(e.target.value))}
              fullWidth size="small" helperText="Raw net weight" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="DS Content (%)" type="number" inputProps={{ step: '0.01' }}
              value={form.ds_content ?? ''}
              onChange={(e) => handleFieldChange('ds_content', e.target.value === '' ? undefined : Number(e.target.value))}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Prorated" type="number" inputProps={{ step: '0.001' }}
              value={form.prorated ?? ''}
              onChange={(e) => handleFieldChange('prorated', e.target.value === '' ? undefined : Number(e.target.value))}
              fullWidth size="small" helperText="e.g. 14.000" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="New Weight (MT)" type="number" inputProps={{ step: '0.0001', min: 0 }}
              value={form.new_weight_mt ?? ''}
              onChange={(e) => handleFieldChange('new_weight_mt', e.target.value === '' ? undefined : Number(e.target.value))}
              fullWidth size="small" helperText="DS-adjusted weight" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Diverted to Composting (MT)" type="number" inputProps={{ step: '0.0001', min: 0 }}
              value={form.diverted_composting_mt ?? 0}
              onChange={(e) => handleFieldChange('diverted_composting_mt', Number(e.target.value))}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12}><Divider /></Grid>
          <Grid item xs={6}>
            <TextField label="Remarks 1" value={form.remarks1 || ''}
              onChange={(e) => handleFieldChange('remarks1', e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Remarks 2" value={form.remarks2 || ''}
              onChange={(e) => handleFieldChange('remarks2', e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Notes" value={form.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              fullWidth size="small" multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : (editingId ? 'Save Changes' : 'Add Record')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ─── Main ─────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <BalanceIcon sx={{ fontSize: '2rem', color: colors.primary[500] }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Substrates Reconciliation</Typography>
          <Typography variant="body2" color="text.secondary">
            Monthly Daily Wastes Monitoring Report – Delivered to Biogas DPO
          </Typography>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Monthly Report" />
          <Tab label="Delivery Records" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>{renderReport()}</TabPanel>
      <TabPanel value={tab} index={1}>{renderRecords()}</TabPanel>

      {renderDialog()}

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #printable-report, #printable-report * { visibility: visible; }
          #printable-report { position: absolute; top: 0; left: 0; width: 100%; }
        }
      `}</style>
    </Box>
  );
};

export default SubstratesReconciliation;
